import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { VehicleInspection } from '../types';

interface VehicleInspectionTableProps {
  inspections: VehicleInspection[];
  onEdit: (inspection: VehicleInspection) => void;
  onDelete: (id: number) => void;
}

const VehicleInspectionTable: React.FC<VehicleInspectionTableProps> = ({
  inspections,
  onEdit,
  onDelete,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inspectionToDelete, setInspectionToDelete] = useState<VehicleInspection | null>(null);

  const handleDeleteClick = (inspection: VehicleInspection) => {
    setInspectionToDelete(inspection);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (inspectionToDelete) {
      onDelete(inspectionToDelete.id);
      setDeleteDialogOpen(false);
      setInspectionToDelete(null);
    }
  };

  const getInspectionStatus = (inspection: VehicleInspection) => {
    if (inspection.unit_type === 'TRUCK') {
      const fields = [
        inspection.cab_card,
        inspection.insurance_card,
        inspection.eld_guide,
        inspection.paper_log,
        inspection.ifta,
        inspection.tractor_annual,
        inspection.fire_extinguisher,
        inspection.triangles,
        inspection.hut_ny,
        inspection.authority,
      ];
      const completedCount = fields.filter(f => f !== undefined).length;
      return `${completedCount}/10`;
    } else {
      const fields = [
        inspection.trailer_card,
        inspection.trailer_annual,
      ];
      const completedCount = fields.filter(f => f !== undefined).length;
      return `${completedCount}/2`;
    }
  };

  const isInspectionComplete = (inspection: VehicleInspection) => {
    if (inspection.unit_type === 'TRUCK') {
      return [
        inspection.cab_card,
        inspection.insurance_card,
        inspection.eld_guide,
        inspection.paper_log,
        inspection.ifta,
        inspection.tractor_annual,
        inspection.fire_extinguisher,
        inspection.triangles,
        inspection.hut_ny,
        inspection.authority,
      ].every(f => f !== undefined);
    } else {
      return [
        inspection.trailer_card,
        inspection.trailer_annual,
      ].every(f => f !== undefined);
    }
  };

  if (inspections.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No pending inspections. Click "Add Unit" to create a new inspection.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Unit Number</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Inspector</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inspections.map((inspection) => (
              <TableRow key={inspection.id}>
                <TableCell>{inspection.unit_number}</TableCell>
                <TableCell>
                  <Chip
                    label={inspection.unit_type === 'TRUCK' ? 'Truck' : 'Trailer'}
                    color={inspection.unit_type === 'TRUCK' ? 'primary' : 'secondary'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={getInspectionStatus(inspection)}
                    color={isInspectionComplete(inspection) ? 'success' : 'warning'}
                    size="small"
                    icon={isInspectionComplete(inspection) ? <CheckCircleIcon /> : undefined}
                  />
                </TableCell>
                <TableCell>
                  {inspection.inspected_by.first_name} {inspection.inspected_by.last_name}
                </TableCell>
                <TableCell>
                  {new Date(inspection.inspection_date).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}{' '}
                  {new Date(inspection.inspection_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' })}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => onEdit(inspection)}
                    title="Edit"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(inspection)}
                    title="Delete"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the inspection for unit "{inspectionToDelete?.unit_number}"?
          This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VehicleInspectionTable;
